import type { AppContext } from '@application/context/AppContext';
import { getTenantProfile, saveTenantProfile } from '@application/persistence/TenantProfileService';
import { logger } from '@infra/telemetry/Logger';
import type { Tenant } from '@shared/types/Tenant';

export type ArchiveTenantResult =
  | { ok: true; tenant: Tenant }
  | { ok: false; error: string };

/**
 * MD-01 — cycle de vie multi-dossiers.
 * Choix produit : archivage logique (`statut: archive`), données conservées
 * (salariés / bulletins / écritures inchangés — pas de cascade destructive).
 */
export class TenantLifecycleService {
  static isArchived(tenant: Tenant | null | undefined): boolean {
    return tenant?.statut === 'archive';
  }

  static async archiveTenant(
    ctx: AppContext,
    tenantId: string,
  ): Promise<ArchiveTenantResult> {
    const session = ctx.auth.getSession();
    if (!session || (session.role !== 'admin' && session.role !== 'super_admin')) {
      return { ok: false, error: 'Seuls les administrateurs peuvent archiver un dossier.' };
    }

    const activeId =
      ctx.tenantContext.tryGetTenant()?.id ??
      ctx.getLicenceRuntime()?.getState()?.claims?.tenant_id ??
      null;
    if (activeId === tenantId) {
      return {
        ok: false,
        error:
          'Fermez d’abord ce dossier (ouvrez une autre entreprise) avant de l’archiver.',
      };
    }

    const profile = await getTenantProfile(ctx, tenantId);
    if (!profile) {
      return { ok: false, error: 'Entreprise introuvable.' };
    }
    if (TenantLifecycleService.isArchived(profile)) {
      return { ok: true, tenant: profile };
    }

    const archived: Tenant = { ...profile, statut: 'archive' };
    await saveTenantProfile(ctx, archived);

    logger.info({
      event: 'tenant_archived',
      tenantId,
      actorId: session.user_id,
    });

    return { ok: true, tenant: archived };
  }

  static async restoreTenant(
    ctx: AppContext,
    tenantId: string,
  ): Promise<ArchiveTenantResult> {
    const session = ctx.auth.getSession();
    if (!session || (session.role !== 'admin' && session.role !== 'super_admin')) {
      return { ok: false, error: 'Seuls les administrateurs peuvent réactiver un dossier.' };
    }

    const profile = await getTenantProfile(ctx, tenantId);
    if (!profile) {
      return { ok: false, error: 'Entreprise introuvable.' };
    }
    if (!TenantLifecycleService.isArchived(profile)) {
      return { ok: true, tenant: profile };
    }

    const restored: Tenant = { ...profile, statut: 'actif' };
    await saveTenantProfile(ctx, restored);

    logger.info({
      event: 'tenant_restored',
      tenantId,
      actorId: session.user_id,
    });

    return { ok: true, tenant: restored };
  }
}
